import type { Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import SmartphoneRounded from '@mui/icons-material/SmartphoneRounded';

/** The offering tier — mirrors CAT `OfferingType` (`Included` | `BuyUp`). */
export type DeviceOfferingTier = 'Included' | 'BuyUp';

export interface DeviceCardModel {
  id: string;
  make: string;
  model: string;
  /** Storage/color variant, appended to the model when present. */
  variant?: string | null;
  tier: DeviceOfferingTier;
  /** Pre-formatted price (e.g. "$0.00" / "$999.00"). */
  priceLabel?: string;
  /** Device thumbnail (CAT master `Device.ImageUrl`); falls back to a glyph when absent. */
  imageUrl?: string | null;
}

export interface DeviceSelectCardProps {
  device: DeviceCardModel;
  selected: boolean;
  onSelect: (id: string) => void;
  /** Tier chip labels (translated). */
  includedLabel: string;
  buyUpLabel: string;
  /** Caption under a buy-up tier ("User pays the carrier directly"). */
  buyUpNote: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Photo-well height. Safe as a plain fixed height now that the well is a block
 * rather than an auto-sizing grid row: the image is `height: 100%` against a
 * definite height, and `overflow: hidden` crops rather than pushing the label
 * block down. (The earlier px-bounded `max-height` existed precisely because the
 * grid row grew to fit a tall portrait render.)
 */
const IMAGE_HEADER_HEIGHT = 110;

/**
 * Selectable device tile (the Polaris kit `DeviceSelectCard`): an image header
 * (the catalog's device thumbnail, glyph when none is configured), the make +
 * model + price, and an offering-tier chip. `Included` (covered by the package,
 * success tone) or `BuyUp` (user pays the carrier directly, accent tone).
 * Selected gets an accent ring + a corner check.
 */
export function DeviceSelectCard({
  device,
  selected,
  onSelect,
  includedLabel,
  buyUpLabel,
  buyUpNote,
  ref,
}: DeviceSelectCardProps) {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const buyUp = device.tier === 'BuyUp';
  const tierColor = buyUp ? theme.palette.secondary : theme.palette.success;
  const modelLabel = device.variant ? `${device.model} ${device.variant}` : device.model;

  return (
    <Box
      component="button"
      type="button"
      ref={ref}
      onClick={() => onSelect(device.id)}
      aria-pressed={selected}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        p: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        bgcolor: 'background.paper',
        border: `2px solid ${selected ? accent : theme.palette.divider}`,
        borderRadius: 2.5,
        boxShadow: selected ? `0 0 0 3px ${alpha(accent, 0.3)}` : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover, &:focus-visible': { borderColor: accent, outline: 'none' },
      }}
    >
      {selected && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 22,
            height: 22,
            borderRadius: '50%',
            zIndex: 2,
            bgcolor: accent,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CheckRounded sx={{ fontSize: 14, color: '#fff' }} />
        </Box>
      )}
      <Box
        aria-hidden
        sx={{
          height: IMAGE_HEADER_HEIGHT,
          flexShrink: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
          // A real render gets the full height of the well: the image is anchored
          // to the top, so leading padding is dead space above it AND, because the
          // crop is `cover`, height given back is height not cropped away. The
          // glyph keeps the inset — it is centered, and a 32px icon flush to the
          // top edge of the well reads as a mistake.
          p: device.imageUrl ? '0 14px 0' : '10px 14px 0',
          bgcolor: 'paperAlt',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {device.imageUrl ? (
          // `cover` + top anchoring, not `contain`: catalog renders are tall
          // portraits, and letterboxing one inside a 110px well left the device
          // a stamp adrift in whitespace. Anchoring the crop to the top keeps
          // the handset's face — the part that identifies it — and sheds the
          // empty bottom instead.
          <Box
            component="img"
            src={device.imageUrl}
            alt=""
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center top',
            }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
            <SmartphoneRounded sx={{ fontSize: 32, color: 'text.secondary' }} />
          </Box>
        )}
      </Box>
      <Box sx={{ p: '8px 9px 9px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <Box>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
            {device.make}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
            {modelLabel}
          </Typography>
        </Box>
        {/* Price shares the chip's row — at this tile width the two together
            still fit on one line, and the pair reads as the single "what does
            this cost me" answer. Wraps rather than clipping if they don't. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
          {device.priceLabel && (
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.3,
                fontVariantNumeric: 'tabular-nums',
                color: buyUp ? 'secondary.dark' : 'text.primary',
              }}
            >
              {device.priceLabel}
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              flexShrink: 0,
              p: '2px 6px',
              borderRadius: 999,
              bgcolor: alpha(tierColor.main, 0.1),
              color: tierColor.dark,
              border: `1px solid ${alpha(tierColor.main, 0.3)}`,
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            {buyUp ? (
              <AddCircleOutlineRounded sx={{ fontSize: 10 }} />
            ) : (
              <CheckCircleOutlineRounded sx={{ fontSize: 10 }} />
            )}
            {buyUp ? buyUpLabel : includedLabel}
          </Box>
        </Box>
        {buyUp && (
          <Typography sx={{ fontSize: 9, lineHeight: 1.4, color: 'text.secondary' }}>
            {buyUpNote}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
